import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const INVALID_LINK =
  "Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş. Yeni bir bağlantı talep edebilirsiniz.";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validatePw = (v) =>
    v.length >= 8 && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(v) && /\d/.test(v);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError(INVALID_LINK); return; }
    if (pw !== pw2) { setError("Şifreler eşleşmiyor."); return; }
    if (!validatePw(pw)) {
      setError("Şifre en az 8 karakter, bir harf ve bir rakam içermelidir.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: pw,
        new_password_confirm: pw2,
      });
      toast.success("Şifreniz başarıyla güncellendi.");
      navigate("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : INVALID_LINK);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative flex items-center gap-3">
          <img src="/penz-icon.png" alt="PENZ"
               className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 p-1 object-contain" />
          <img src="/penz-logo.png" alt="PENZ"
               className="h-6 w-auto object-contain brightness-0 invert" />
        </div>
        <div className="relative max-w-md">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Yeni Şifre</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight leading-tight">
            Şifrenizi <span className="text-emerald-400">yenileyin</span> ve devam edin.
          </h1>
          <div className="mt-8 flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Bağlantı tek kullanımlıktır</span>
          </div>
        </div>
        <div className="relative text-xs text-white/40">© {new Date().getFullYear()} PENZ</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <img src="/penz-icon.png" alt="PENZ" className="w-9 h-9 rounded-lg object-contain" />
            <img src="/penz-logo.png" alt="PENZ" className="h-5 w-auto object-contain" />
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Şifre Sıfırla</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 mt-1">
            Yeni Şifre Belirleyin
          </h2>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="reset-password-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Yeni Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="pl-9 pr-10 h-11 rounded-lg border-slate-200"
                  placeholder="En az 8 karakter"
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShow((v) => !v)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-900"
                        aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
                        tabIndex={-1}
                        data-testid="reset-password-toggle">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                En az 8 karakter, bir harf ve bir rakam içermelidir.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Yeni Şifre (tekrar)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="pl-9 h-11 rounded-lg border-slate-200"
                  placeholder="Şifreyi tekrar girin"
                  required
                  minLength={8}
                  data-testid="reset-password-confirm-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-xs px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-100"
                   data-testid="reset-password-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium"
              data-testid="reset-password-submit-button"
            >
              {loading ? "Güncelleniyor..." : "Şifremi Güncelle"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-slate-700 hover:text-slate-900"
              data-testid="reset-password-back-to-login"
            >
              Giriş ekranına dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
